import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import { getLength } from 'ol/sphere';
import {IDrawZone} from './components/kl-draw-zone-sidebar/kl-draw-zone-sidebar';
import {isEmpty} from 'lodash-es';
import {validate} from 'vee-validate';
import WKT from "ol/format/WKT";
import MultiPolygon from 'ol/geom/MultiPolygon';
import {IDrawZoneMapGeometry} from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-map';
import * as yup from 'yup';
import {ValidationError} from 'yup';
import {klValidateYup} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';

export enum EDrawZoneMapFeaturePropertyKey {
    drawZoneId = 'drawZoneId',
    geometryInfo = 'geometryInfo',
    orgZoneInfo = 'orgZoneInfo',
}

export default class KlDrawZoneMapUtils {

    public static removeDuplicateCoordinates(feature: Feature, allowMultiPolygon: boolean): boolean {
        // Legacy function: i'm not entirely sure why this is necessary & if this function is called at the best locations.
        //

        // Purpose: perform some geometry cleaning & corrections
        // - remove duplicate points
        // - close polygons
        // - remove holes?


        if (!feature || !feature.getGeometry()) {
            return false;
        }
        if (feature.getGeometry().getType() === 'MultiPolygon') {
            // TODO: refactor into proper Polygon/MultiPolygon support
            // for now: we leave the MultiPolygons alone
            console.assert(allowMultiPolygon, `removeDuplicateCoordinates: unexpected "MultiPolygon"`);
            return true;
        }
        if (feature.getGeometry().getType() !== 'Polygon') {
            console.warn(`Expected geometry type "Polygon", but received "${feature.getGeometry().getType()}"`);
            return false;
        }

        const polygonGeometry = feature.getGeometry() as Polygon;
        const origCoordinates = polygonGeometry.getCoordinates();
        const allowedCoordinates = [origCoordinates[0]]; // only use first ring (= no holes?)
        const cleanCoordinates = allowedCoordinates.map((cc: number[][]) => {
            return KlDrawZoneMapUtils._cleanupPolygonCoordinates(cc);
        });

        feature.setGeometry(new Polygon(cleanCoordinates));

        return true;
    }

    private static _cleanupPolygonCoordinates(coordinates: number[][]): number[][] {
        const getKey = (coordinate: number[]): string => coordinate[0] + '' + coordinate[1];

        const dictionary: any = {};
        const filteredPolygonCoordinates = [];

        // filter duplicates
        for (let i = 0; i < coordinates.length; i++) {
            const key = getKey(coordinates[i]);
            if (!dictionary[key]) {
                dictionary[key] = coordinates[i];
                filteredPolygonCoordinates.push(coordinates[i]);
            }
        }

        // close polygon
        filteredPolygonCoordinates.push(coordinates[0]);

        return filteredPolygonCoordinates;
    }

    private static _drawZoneIdCounter: number = 1;

    public static getCrsPropertiesName(): string {
        // ['EPSG:31370', 'EPSG:3812'] > ['urn:ogc:def:crs:EPSG::31370', 'urn:ogc:def:crs:EPSG::3812']

        if (!import.meta.env.VITE_MAP_EPSG_DEFAULT) {
            return 'urn:ogc:def:crs:EPSG::3812';
        }

        const result = (import.meta.env.VITE_MAP_EPSG_DEFAULT as string).replace('EPSG:', 'urn:ogc:def:crs:EPSG::');
        return result;
    }

    public static createDrawZone(feature: Feature): IDrawZone {
        const result: IDrawZone = {
            id: KlDrawZoneMapUtils._drawZoneIdCounter++,
            name: 'zone-' + (KlDrawZoneMapUtils._drawZoneIdCounter - 1), // TEMP TEMP TEMP
            coordinates: null,
            area: -1,
            length: -1,
            type: feature?.getGeometry()?.getType(), // 'Polygon', // = type
            crs: {
                type: 'name',
                properties: {
                    name: KlDrawZoneMapUtils.getCrsPropertiesName(),
                },
            },
        };
        KlDrawZoneMapUtils.updateDrawZone(result, feature);

        return result;
    }

    public static updateDrawZone(zone: IDrawZone, feature: Feature) {
        if (!feature.getGeometry()) {
            // feature without geometry
            return;
        }

        const geometry = feature.getGeometry();
        const type = geometry.getType();

        if (type === 'MultiPolygon') {
            // TODO: waar, wie, wat??
            // debugger;

            // TODO: refactor into proper Polygon/MultiPolygon support?
            const multiPolygonGeometry = geometry as MultiPolygon;
            zone.coordinates = multiPolygonGeometry.getCoordinates();
            zone.area = multiPolygonGeometry.getArea();
            zone.length = 0; // te ingewikkeld bij MultiPolygon
            return;
        }
        if (type !== 'Polygon') {
            console.error(`createDrawZone: expected a Polygon, but received ${type}`);
        }

        const polygonGeometry = geometry as Polygon;
        const polygonCoordinates = polygonGeometry.getCoordinates();
        const cleanCoordinates = [KlDrawZoneMapUtils._cleanupPolygonCoordinates(polygonCoordinates[0])];
        const cleanFeature = new Feature({
            geometry: new Polygon(cleanCoordinates)
        });

        zone.coordinates = cleanCoordinates;
        zone.area = KlDrawZoneMapUtils.getArea(cleanFeature);
        zone.length = KlDrawZoneMapUtils.getCircumference(cleanFeature);
    }

    public static getCircumference(polygonFeature: Feature): number {
        if (!polygonFeature || !polygonFeature.getGeometry()) {
            return 0;
        }
        return Math.ceil(getLength(polygonFeature.getGeometry()));
    }

    public static getArea(feature: Feature): number {
        if (!feature || !feature.getGeometry()) {
            return 0;
        }

        if (feature.getGeometry().getType() !== 'Polygon') {
            return KlDrawZoneMapUtils.getPolygonArea(feature.getGeometry() as Polygon);
        }
        if (feature.getGeometry().getType() !== 'MultiPolygon') {
            return KlDrawZoneMapUtils.getPolygonArea(feature.getGeometry() as MultiPolygon);
        }

        console.warn(`getArea: unexpected feature.getGeometry() type '${feature.getGeometry().getType()}'`);
        return 0;
    }

    public static getPolygonArea(polygon: Polygon | MultiPolygon): number {
        if (!polygon) {
            return 0;
        }
        return Math.ceil(polygon.getArea());
    }

    public static formatArea(area: number): string {
        const rounded = Math.ceil(area);
        const useKm2 = rounded >= 1000000;
        const suffix = useKm2 ? 'km²' : 'm²';

        let areaString: string;
        if (useKm2) {
            const nrOfFractionDigits = rounded > 100000000 ? 0 : 2;
            const roundedInKm2 = (rounded / 1000000).toFixed(nrOfFractionDigits);
            areaString = roundedInKm2.toString().replace('.', ',');
        }
        else {
            areaString = rounded.toString();
        }

        const areaStringFormatted = areaString.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return `${areaStringFormatted} ${suffix}`;
    }

    public static formatAreaSimple(area: number): string {
        const rounded = Math.ceil(area);
        const areaStringFormatted: string = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return `${areaStringFormatted} m²`;
    }

    public static updateZoneNames(zones: IDrawZone[], reference: string, manualOverrides: {[id: number]: string}) {
        if (isEmpty(zones)) {
            return;
        }

        const maxLength = 90;

        for (let i = 0; i < zones.length; i++) {
            if (!isEmpty(manualOverrides) && manualOverrides[zones[i].id]) {
                zones[i].name = manualOverrides[zones[i].id];
            }
            else {
                if (isEmpty(reference)) {
                    // no reference
                    zones[i].name = `Zone van ${KlDrawZoneMapUtils.formatArea(zones[i].area)}`;
                }
                else if (zones.length === 1) {
                    // 1 zone
                    zones[i].name = reference;
                }
                else {
                    const safeReference = reference?.substring(0, maxLength);
                    zones[i].name = `${safeReference} (${i+1}/${zones.length})`;
                }
            }
        }
    }

    public static createFeatureFromDrawZoneMapGeometry(zone: IDrawZoneMapGeometry): Feature {

        // use WKT || coordinates, NOT both
        // maar bij de emit('input') gaan we ze wel zelfs beide invullen??

        // WKT
        if (zone.wkt) {
            return new WKT().readFeature(zone.wkt);
        }

        // Empty Feature: feature without geometry (ex. KLIM-zone)
        if (isEmpty(zone.coordinates)) {
            return new Feature();
        }

        // coordinates: number[][][] > Polygon (coordinates = Array of linear rings; linear ring = array of coordinates; coordinate = [x, y])
        const isPolygon =
            Array.isArray(zone.coordinates) &&
            Array.isArray(zone.coordinates[0]) &&
            Array.isArray(zone.coordinates[0][0]) &&
            !Array.isArray(zone.coordinates[0][0][0]);
        if (isPolygon) {
            return new Feature({ geometry: new Polygon(zone.coordinates as number[][][]) });
        }

        // coordinates: number[][][][] > MultiPolygon (coordinates = Array of Polygon coordinates)
        const isMultiPolygon =
            Array.isArray(zone.coordinates) &&
            Array.isArray(zone.coordinates[0]) &&
            Array.isArray(zone.coordinates[0][0]) &&
            Array.isArray(zone.coordinates[0][0][0]) &&
            !Array.isArray(zone.coordinates[0][0][0][0]);
        if (isMultiPolygon) {
            return new Feature({ geometry: new MultiPolygon(zone.coordinates as number[][][][]) });
        }

        throw new Error('ArgumentException: invalid coordinates');
    }

    public static async validateWktGeometry(wkt: string, isRequired: boolean, rules: yup.Schema): Promise<{ valid: boolean, errors?: string[]}> {

        const result = {
            valid: false,
            errors: [],
        };

        if (!wkt) {
            if (isRequired) {
                result.valid = false;
                result.errors.push('Geen geometry aanwezig.');
                return result;
            }
            return { valid: true };
        }

        const feature = new WKT().readFeature(wkt);
        const drawZone = KlDrawZoneMapUtils.createDrawZone(feature);

        return await klValidateYup(drawZone, rules);
    }
}
